import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { ProjectTemplateDetailsResource, PublishedAndSharedProjectTemplateSummary } from "@octopusdeploy/octopus-server-client";
import classNames from "classnames";
import type { ReactNode } from "react";
import { useState } from "react";
import { UpdateBehaviourContent, VersionSelectionContent } from "~/areas/projects/components/Templates/TemplateVersionSelection";
import { getVersionMask, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import { repository } from "~/clientInstance";
import { CustomDialogContent } from "~/components/DialogLayout/Custom";
import CustomSaveDialogLayout from "~/components/DialogLayout/Custom/CustomSaveDialogLayout";
import { useErrors } from "~/components/ErrorContext/ErrorContext";
import { GuidedSetupFrame } from "../OnboardingDialog/GuidedSetupFrame";
import addProjectImageDark from "./assets/img-addproject-dark.svg";
import addProjectImageLight from "./assets/img-addproject-light.svg";
import styles from "./styles.module.less";
interface ProjectTemplateVersionSelectionPageProps {
    selectedTemplate: PublishedAndSharedProjectTemplateSummary;
    versionSummary: {
        latestStableVersion: string | null;
        latestPreReleaseVersion: string | null;
    };
    onVersionSelected: (templateDetails: ProjectTemplateDetailsResource) => void;
    createProject: (templateDetails: ProjectTemplateDetailsResource) => Promise<boolean>;
    back: () => void;
    close: () => void;
    isOnboarding?: boolean;
    title?: ReactNode;
    nextButtonTitle?: string;
    hideBackButton?: boolean;
}
const FullscreenFrame: React.FC<{
    children?: React.ReactNode;
}> = ({ children }) => <div className={styles.noFrameFlex}>{children}</div>;
FullscreenFrame.displayName = "FullscreenFrame"
function ProjectTemplateVersionSelectionPage(props: ProjectTemplateVersionSelectionPageProps) {
    const defaultToPreRelease = !props.versionSummary.latestStableVersion && !!props.versionSummary.latestPreReleaseVersion;
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(defaultToPreRelease ? UsageConstraint.PreRelease : UsageConstraint.Stable);
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(defaultToPreRelease ? UpdateBehaviour.PreRelease : UpdateBehaviour.Minor);
    const [isCreating, setIsCreating] = useState(false);
    const errors = useErrors();
    const template = props.selectedTemplate;
    const { latestStableVersion, latestPreReleaseVersion } = props.versionSummary;
    const selectedVersion = selectedUsageConstraint === UsageConstraint.PreRelease ? latestPreReleaseVersion : latestStableVersion;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const versionMask = selectedUsageConstraint === UsageConstraint.PreRelease ? "PreRelease" : getVersionMask(selectedVersion!, selectedUpdateBehaviour);
    const notifyVersionSelected = (usageConstraint: UsageConstraint, updateBehaviour: UpdateBehaviour) => {
        const version = usageConstraint === UsageConstraint.PreRelease ? latestPreReleaseVersion : latestStableVersion;
        if (version) {
            const mask = usageConstraint === UsageConstraint.PreRelease ? "PreRelease" : getVersionMask(version, updateBehaviour);
            props.onVersionSelected({ Slug: template.Slug, Version: version, VersionMask: mask });
        }
    };
    const handleUsageConstraintChanged = (newConstraint: UsageConstraint) => {
        setSelectedUsageConstraint(newConstraint);
        notifyVersionSelected(newConstraint, selectedUpdateBehaviour);
    };
    const handleUpdateBehaviourChanged = (newBehaviour: UpdateBehaviour) => {
        setSelectedUpdateBehaviour(newBehaviour);
        notifyVersionSelected(selectedUsageConstraint, newBehaviour);
    };
    const handleNext = async () => {
        if (!selectedVersion || isCreating)
            return false;
        setIsCreating(true);
        try {
            return await props.createProject({ Slug: template.Slug, Version: selectedVersion, VersionMask: versionMask });
        }
        finally {
            setIsCreating(false);
        }
    };
    const helpPanelContent = (<div>
            <div>
                Configure how your project will receive updates from the <strong>{props.selectedTemplate.Name}</strong> template.
            </div>
        </div>);
    return (<GuidedSetupFrame helpPanelImage={{ src: addProjectImageLight, altText: "Configure Template Version", darkThemeSrc: addProjectImageDark }} helpPanelContent={helpPanelContent} onClose={props.close} showThemeSelector={props.isOnboarding}>
            <div className={noFrameFlexFullScreen}>
                <CustomSaveDialogLayout frame={FullscreenFrame} onSaveClick={handleNext} close={props.close} open={true} errors={errors} renderTitle={() => <div>{props.title}</div>} renderActions={(renderProps) => (<div className={classNames(styles.buttonContainer, styles.buttonsContainerFullScreen, styles.templateVersionSelectionButtons)}>
                            {!props.hideBackButton && <Button importance="quiet" label="Back" onClick={() => props.back()}/>}
                            <ActionButton type={ActionButtonType.Save} label={props.nextButtonTitle ?? (props.isOnboarding ? "Next" : "Create Project")} busyLabel="Creating..." onClick={renderProps.onSaveClick} disabled={!selectedVersion || isCreating}/>
                        </div>)} renderContent={() => (<CustomDialogContent className={classNames(styles.customDialogContentFullScreen, styles.templateVersionSelectionContent)}>
                            <div className={pageStyles.templateInfo}>
                                <Avatar type="template" size="medium" src={repository.Icons.getIconUrl(props.selectedTemplate.Icon ?? undefined)} alt={`Icon for ${props.selectedTemplate.Name}`}/>
                                <div className={pageStyles.templateInfoText}>
                                    <div className={pageStyles.templateName}>{props.selectedTemplate.Name}</div>
                                    {props.selectedTemplate.Description && <div className={pageStyles.templateDescription}>{props.selectedTemplate.Description}</div>}
                                </div>
                            </div>

                            <div className={pageStyles.sectionHeading}>Configure template version</div>

                            <div className={pageStyles.versionSelectionContainer}>
                                <VersionSelectionContent iconLink={repository.Icons.getIconUrl(props.selectedTemplate.Icon ?? undefined)} heading={props.selectedTemplate.Name} versionSummary={{ LatestStableVersion: props.versionSummary.latestStableVersion, LatestPreReleaseVersion: props.versionSummary.latestPreReleaseVersion }} selectedUsageConstraint={selectedUsageConstraint} onUsageConstraintChanged={handleUsageConstraintChanged} hideBanner/>
                                {selectedUsageConstraint !== UsageConstraint.PreRelease && (<UpdateBehaviourContent iconLink={repository.Icons.getIconUrl(props.selectedTemplate.Icon ?? undefined)} heading={props.selectedTemplate.Name} versionSummary={{ LatestStableVersion: props.versionSummary.latestStableVersion, LatestPreReleaseVersion: props.versionSummary.latestPreReleaseVersion }} selectedUpdateBehaviour={selectedUpdateBehaviour} onUpdateBehaviourChanged={handleUpdateBehaviourChanged} hideBanner/>)}
                            </div>
                        </CustomDialogContent>)}/>
            </div>
        </GuidedSetupFrame>);
}
const noFrameFlexFullScreen = css({
    marginTop: space[40],
    height: "100%",
    "& > div": {
        padding: 0,
    },
});
const pageStyles = {
    templateInfo: css({
        display: "flex",
        alignItems: "flex-start",
        gap: space[12],
        marginBottom: space[24],
    }),
    templateInfoText: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        flex: 1,
    }),
    sectionHeading: css({
        font: text.heading.medium,
        marginBottom: space[24],
    }),
    templateName: css({
        font: text.heading.medium,
    }),
    templateDescription: css({
        font: text.body.regular.medium,
        color: "var(--text-secondary)",
    }),
    versionSelectionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[24],
    }),
};
export default ProjectTemplateVersionSelectionPage;
